import { Skeleton, Switch } from 'antd'
import dayjs from 'dayjs'
import { useContext, useEffect, useMemo, useState } from 'react'

import { API } from '@/api'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Button, Divider, Loading, Trans } from '@/components'
import { ORDER_CATALOG } from '@/consts/order'
import { AppContext } from '@/contexts/AppContext'
import { ExchargeModal } from '@/pages/billing/components/RechargeModal.tsx'
import DriveVolumeSection from '@/pages/space-mgr/components/drive-volume-section.tsx'
import ImageVolumeSection from '@/pages/space-mgr/components/image-volume-section.tsx'
import { useUserStore } from '@/stores/user'
import { formatDomain } from '@/utils/formatter'
import { formatGB, getEffectiveQuota } from '@/utils/storage'
import { BILLING_SWITCH_ENTRY_ENABLED, getStorageBillingMode } from '@/utils/storage-billing'
import { cn } from '@/utils/style'

import ExpansionModal from './components/expansion-modal'
import SnapVolumeSection from './components/snap-volume-section.tsx'
import { SwitchBillingBanner } from './components/switch-billing-banner'
import { SwitchBillingModal } from './components/switch-billing-modal'
import VolumeQuotaModal from './components/volume-quota-modal'
import {
  getDatadriveGradientClass,
  VolumeUsageSegmentedBar,
} from './components/volume-usage-segmented-bar'

export function SpaceMgrPage() {
  const userStore = useUserStore()
  const [expansionModalOpen, setExpansionModalOpen] = useState(false)
  const [volumeQuotaModalOpen, setVolumeQuotaModalOpen] = useState(false)
  const [autoRenewal, setAutoRenewal] = useState(userStore.order?.autoRenewal || false)
  const [updatingAutoRenewal, setUpdatingAutoRenewal] = useState(false)

  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [snapshotVolumes, setSnapshotVolumes] = useState<Volume[]>()
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>()
  const [imageVolumes, setImageVolumes] = useState<Volume[]>()

  const [imageOnSpaceBilling, setImageOnSpaceBilling] = useState(true)
  const [snapshotOnSpaceBilling, setSnapshotOnSpaceBilling] = useState(true)

  const [switchBillingModalOpen, setSwitchBillingModalOpen] = useState(false)
  const [exchargeModalOpen, setExchargeModalOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const app = useContext(AppContext)

  const getDomains = () => {
    setLoading(true)
    API.getDomainList()
      .then((res: any) => {
        if (res.code === 0) {
          // todo: remove this after the backend is fixed
          const filteredDomainInfos = (res.domainInfos || []).filter(
            (info: any) => info.domain !== 3,
          )
          setDomainInfos(filteredDomainInfos)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await API.getVolumes()
      if (response?.code == 0) {
        const volumes = response?.volumes || []

        setSnapshotVolumes(volumes.filter((it: any) => it.type == 2))
        setCloudVolumes(volumes.filter((it: any) => it.type == 1))
        setImageVolumes(volumes.filter((it: any) => it.type == 3))

        const mode = getStorageBillingMode(volumes)
        setImageOnSpaceBilling(mode.imageOnSpaceBilling)
        setSnapshotOnSpaceBilling(mode.snapshotOnSpaceBilling)
      }

      await Promise.all([userStore.getVolume(), userStore.getOrder()])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDomains()
    loadData()
    app.refresh()
  }, [])

  const handleAutoRenewalToggle = async (checked: boolean) => {
    setUpdatingAutoRenewal(true)
    try {
      await API.updateOrderAutoRenewal({
        auto_renewal: checked,
        catalog: ORDER_CATALOG.STORAGE,
        type: 1,
      })
      setAutoRenewal(checked)
      await userStore.getOrder()
    } catch (error) {
      console.error('Failed to update auto renewal:', error)
      setAutoRenewal(!checked)
    } finally {
      setUpdatingAutoRenewal(false)
    }
  }

  useEffect(() => {
    if (userStore.order?.autoRenewal !== undefined) {
      setAutoRenewal(userStore.order.autoRenewal)
    }
  }, [userStore.order?.autoRenewal])

  const anyOnSpaceBilling = imageOnSpaceBilling || snapshotOnSpaceBilling
  const bothSwitched = !imageOnSpaceBilling && !snapshotOnSpaceBilling

  const handleSwitchBillingClick = async () => {
    await userStore.getAccount().catch(() => {})
    if ((useUserStore.getState().balance || 0) <= 0) {
      setExchargeModalOpen(true)
      return
    }
    setSwitchBillingModalOpen(true)
  }

  const allocatedQuotaUsageInGB = (userStore.partVolume?.usage_mb || 0) / 1024
  const partVolumeQuotaInGB = userStore.isOrderExpired
    ? userStore.partVolume?.last_quota_gb || 0
    : userStore.partVolume?.quota_gb || 0

  // Calculate image volume available quota
  const snapshotQuotaSum = (snapshotVolumes || []).reduce((sum, vol) => {
    return sum + getEffectiveQuota(vol, userStore.isOrderExpired)
  }, 0)

  const cloudQuotaSum = (cloudVolumes || []).reduce((sum, vol) => {
    return sum + getEffectiveQuota(vol, userStore.isOrderExpired)
  }, 0)

  const imageVolumeAvailableQuota = Math.max(
    0,
    partVolumeQuotaInGB - snapshotQuotaSum - cloudQuotaSum,
  )

  // Calculate total actual usage from all volumes
  const totalActualUsageInGB = useMemo(() => {
    const allVolumes = [
      ...(snapshotVolumes || []),
      ...(cloudVolumes || []),
      ...(imageVolumes || []),
    ]
    return allVolumes.reduce((sum, volume) => sum + volume.usage_mb / 1024, 0)
  }, [snapshotVolumes, cloudVolumes, imageVolumes])

  return (
    <div className="flex flex-col h-full">
      <div className="card bg-panel-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="h6">
            <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
          </div>
          {!userStore.order ||
            (!userStore.isOrderExpired && (
              <div className="flex items-center gap-2">
                <span className="t-sb">
                  <Trans i18nKey="space.autoRenewalTitle">Auto Renewal</Trans>
                </span>
                <Switch
                  checked={autoRenewal}
                  loading={updatingAutoRenewal}
                  onChange={handleAutoRenewalToggle}
                  checkedChildren={<Trans i18nKey="space.autoRenewalOn">ON</Trans>}
                  unCheckedChildren={<Trans i18nKey="space.autoRenewalOff">OFF</Trans>}
                />
              </div>
            ))}
        </div>

        <div className="my-4">
          <VolumeUsageSegmentedBar
            snapshotVolumes={snapshotVolumes}
            cloudVolumes={cloudVolumes}
            imageOnSpaceBilling={imageOnSpaceBilling}
            snapshotOnSpaceBilling={snapshotOnSpaceBilling}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-4 items-center">
            <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-neutral-200">
              <div className="text-primary t-sb">
                <Trans
                  i18nKey="space.titleUsage"
                  values={{
                    usage: formatGB(totalActualUsageInGB, {
                      dynamicDecimals: true,
                      includeUnit: false,
                    }),
                    quota: formatGB(partVolumeQuotaInGB, {
                      dynamicDecimals: true,
                      includeUnit: false,
                    }),
                  }}
                  components={[
                    <span className="text-secondary">Using </span>,
                    <span className="text-secondary"> of </span>,
                  ]}
                >
                  Using{' '}
                  {{
                    usage: formatGB(totalActualUsageInGB, {
                      dynamicDecimals: true,
                      includeUnit: false,
                    }),
                  }}{' '}
                  GB of{' '}
                  {{
                    quota: formatGB(partVolumeQuotaInGB, {
                      dynamicDecimals: true,
                      includeUnit: false,
                    }),
                  }}{' '}
                  GB
                </Trans>
              </div>
            </div>

            {userStore.order && (
              <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-neutral-200">
                <div className="text-primary t-sb flex items-center space-x-1">
                  <div className="text-secondary">
                    <Trans i18nKey="space.expireOn">Expires on</Trans>
                  </div>
                  <div>
                    {userStore.order?.deadlineTime?.seconds ? (
                      dayjs(userStore.order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')
                    ) : (
                      <Skeleton.Input active size="small" style={{ width: 100 }} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {userStore.isOrderExpired && (
              <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-neutral-200">
                <div className="text-primary t-sb flex items-center space-x-1">
                  <div className="text-secondary">
                    <Trans i18nKey="space.dataPurgeOn">Data Purge on</Trans>
                  </div>
                  <div className="text-error">
                    {userStore.order?.extra?.purgeTime?.seconds ? (
                      dayjs(userStore.order.extra.purgeTime.seconds * 1000).format('YYYY-MM-DD')
                    ) : (
                      <Skeleton.Input active size="small" style={{ width: 100 }} />
                    )}
                  </div>
                </div>
              </div>
            )}

            <div
              className="text-btn-secondary font-bold cursor-pointer t-sb"
              onClick={() => setExpansionModalOpen(true)}
            >
              <Trans i18nKey="space.upgrade">Upgrade</Trans>
            </div>
          </div>
          {bothSwitched
            ? (cloudVolumes || []).some((vol) => (vol.quota_gb || vol.last_quota_gb) > 0) && (
                <div>
                  <div className="flex items-baseline t-sm text-primary gap-2">
                    {(cloudVolumes || []).map((volume, index) => (
                      <div
                        key={`legend.${volume.id}.${index}`}
                        className="flex items-baseline gap-2"
                      >
                        <div
                          className={cn(
                            'w-3 h-3',
                            index > 0 && 'ml-2',
                            getDatadriveGradientClass(index, userStore.isOrderExpired),
                          )}
                        />
                        {domainInfos.find((d) => d.domain === volume.domain)?.displayName ||
                          formatDomain(volume.domain)}
                      </div>
                    ))}
                    <div className="w-3 h-3 ml-2 bg-neutral-200" />
                    <Trans i18nKey="space.legendFreeSpace">Free Space</Trans>
                  </div>
                </div>
              )
            : allocatedQuotaUsageInGB > 0 && (
                <div>
                  <div className="flex items-baseline t-sm text-primary gap-2">
                    {snapshotOnSpaceBilling && (
                      <>
                        <div
                          className={cn(
                            ' w-3 h-3',
                            userStore.isOrderExpired ? 'bg-bar-tertiary' : ' bg-btn-tertiary',
                          )}
                        />
                        <Trans i18nKey="space.volumeItemSubTitleSnapshot">Snapshot</Trans>
                      </>
                    )}
                    <div
                      className={cn(
                        ' w-3 h-3 ml-2',
                        userStore.isOrderExpired ? 'bg-bar-tertiary/60' : 'bg-btn-secondary',
                      )}
                    />
                    <Trans i18nKey="space.volumeItemSubTitleCloudStorage">Datadrive</Trans>
                    {imageOnSpaceBilling && (
                      <>
                        <div
                          className={cn(
                            ' w-3 h-3 ml-2',
                            userStore.isOrderExpired ? 'bg-tertiary' : 'bg-active',
                          )}
                        />
                        <Trans i18nKey="space.volumeItemSubTitleImage">Image</Trans>
                      </>
                    )}
                  </div>
                </div>
              )}
        </div>

        {userStore.isOrderExpired && (
          <div className={cn('t-sm ', 'text-error', 'mt-4')}>
            <Trans i18nKey="space.volumeItemExpired">
              Your storage space has expired, please upgrade your plan
            </Trans>
          </div>
        )}
      </div>

      <div className="card bg-panel-white mt-2.5 h-full overflow-y-auto">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="h6">
              <Trans i18nKey="space.titleQuota">Quota</Trans>
            </div>
            {BILLING_SWITCH_ENTRY_ENABLED && !loading && anyOnSpaceBilling && (
              <SwitchBillingBanner onClick={handleSwitchBillingClick} />
            )}
          </div>

          <Button
            type="secondary"
            disabled={!partVolumeQuotaInGB}
            onClick={() => setVolumeQuotaModalOpen(true)}
          >
            <Trans i18nKey="space.modify">Modify</Trans>
          </Button>
        </div>
        <Divider className="mb-4" />

        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <div className="mb-2 flex flex-col gap-4">
            {imageOnSpaceBilling && (
              <ImageVolumeSection
                volumes={imageVolumes || []}
                availableQuota={imageVolumeAvailableQuota}
              />
            )}
            {snapshotOnSpaceBilling && <SnapVolumeSection volumes={snapshotVolumes || []} />}
            <DriveVolumeSection volumes={cloudVolumes || []} domainInfos={domainInfos} />
          </div>
        )}
      </div>

      {expansionModalOpen ? (
        <ExpansionModal
          open={expansionModalOpen}
          onClose={() => setExpansionModalOpen(false)}
          onOk={async () => {
            setExpansionModalOpen(false)
            await userStore.getUserInfo()
            await loadData()
          }}
        ></ExpansionModal>
      ) : null}

      {volumeQuotaModalOpen ? (
        <VolumeQuotaModal
          open={volumeQuotaModalOpen}
          onOk={() => {
            setVolumeQuotaModalOpen(false)
            loadData()
            app.refresh()
          }}
          onClose={() => setVolumeQuotaModalOpen(false)}
          data={{
            snapshotVolumes: snapshotVolumes,
            cloudVolumes: cloudVolumes,
            imageVolumes: imageVolumes,
            domainInfos: domainInfos,
          }}
          imageOnSpaceBilling={imageOnSpaceBilling}
          snapshotOnSpaceBilling={snapshotOnSpaceBilling}
        ></VolumeQuotaModal>
      ) : null}

      {switchBillingModalOpen && (
        <SwitchBillingModal
          open={switchBillingModalOpen}
          imageOnSpaceBilling={imageOnSpaceBilling}
          snapshotOnSpaceBilling={snapshotOnSpaceBilling}
          onOk={() => {
            setSwitchBillingModalOpen(false)
            loadData()
            app.refresh()
          }}
          onClose={() => {
            setSwitchBillingModalOpen(false)
            loadData()
          }}
        />
      )}

      {exchargeModalOpen && (
        <ExchargeModal
          onOk={() => {
            setExchargeModalOpen(false)
            userStore.getAccount()
            loadData()
          }}
          onClose={() => {
            setExchargeModalOpen(false)
          }}
        />
      )}
    </div>
  )
}
